import { useState } from "react";
import { useLessonsForNavigation } from "../lessons/useLessonsForNavigation";
import { useLessonsWithLimitation } from "../lessons/useLessonsWithLimitation";
import { useProjectsForNavigation } from "../projects/useProjectsForNavigation";
import { useSlidesByLessonId } from "../slides/useSlidesByLessonId";

interface NavigationItem {
  id: string;
  name: string;
  type: "project" | "lesson";
}

interface UseLessonNavigationOptions {
  /** Whether to fetch lessons with the expensive limitation field (default: false) */
  includeLimitation?: boolean;
  /** Whether to fetch slides when navigated into a lesson (default: false) */
  includeSlides?: boolean;
  /** Full ancestor project stack to pre-populate navigation (enables correct back navigation) */
  initialProjectStack?: Array<{ id: string; name: string }> | undefined;
  /** Pre-select a lesson as the default destination */
  initialLessonId?: string | null | undefined;
}

/**
 * Hook for managing hierarchical navigation through projects and lessons in modals.
 *
 * Provides state and methods for navigating through a tree structure of projects
 * and lessons, including breadcrumb navigation, search functionality, and loading states.
 * Used primarily in modals like MoveSlideModal where users need to navigate through
 * the project/lesson hierarchy to select a destination.
 *
 * @param searchText - Current search query string
 * @param options - Optional configuration
 * @param options.includeLimitation - Fetch lessons with limitation field (expensive, default: false)
 * @param options.includeSlides - Fetch slides when inside a lesson (default: false)
 * @returns Navigation state and control methods
 *
 * @example
 * // Basic usage (for move/duplicate slide modals)
 * const { projects, lessons, ... } = useLessonNavigation(searchText);
 *
 * @example
 * // With limitation and slides (for WordGrammarLimitForm)
 * const { projects, lessons, slides, ... } = useLessonNavigation(searchText, {
 *   includeLimitation: true,
 *   includeSlides: true,
 * });
 */
export function useLessonNavigation(searchText: string, options: UseLessonNavigationOptions = {}) {
  const {
    includeLimitation = false,
    includeSlides = false,
    initialProjectStack,
    initialLessonId,
  } = options;

  const [currentProjectId, setCurrentProjectId] = useState<string | null>(
    initialProjectStack?.at(-1)?.id ?? null
  );
  const [currentLessonId, setCurrentLessonId] = useState<string | null>(initialLessonId ?? null);
  const [navigationStack, setNavigationStack] = useState<NavigationItem[]>(
    initialProjectStack?.map((p) => ({ ...p, type: "project" as const })) ?? []
  );

  // Fetch projects
  const {
    projects,
    loading: projectLoading,
    loadMore: loadMoreProjects,
    hasMore: hasMoreProjects,
  } = useProjectsForNavigation({
    parentId: currentProjectId,
    searchText,
  });

  // Fetch lessons - use expensive hook only when limitation is needed
  const {
    lessons: lessonsWithLimitation,
    loading: lessonWithLimitationLoading,
    loadMore: loadMoreLessonWithLimitation,
    hasMore: hasMoreLessonWithLimitation,
  } = useLessonsWithLimitation({
    parentId: currentProjectId,
    searchText,
    skip: !includeLimitation,
  });

  const {
    lessons: lessonsWithoutLimitation,
    loading: lessonWithoutLimitationLoading,
    loadMore: loadMoreLessonWithoutLimitation,
    hasMore: hasMoreLessonWithoutLimitation,
  } = useLessonsForNavigation({
    parentId: currentProjectId,
    searchText,
    skip: includeLimitation,
  });

  const lessons = includeLimitation ? lessonsWithLimitation : lessonsWithoutLimitation;
  const lessonLoading = includeLimitation
    ? lessonWithLimitationLoading
    : lessonWithoutLimitationLoading;
  const loadMoreLesson = includeLimitation
    ? loadMoreLessonWithLimitation
    : loadMoreLessonWithoutLimitation;
  const hasMoreLesson = includeLimitation
    ? hasMoreLessonWithLimitation
    : hasMoreLessonWithoutLimitation;

  // Fetch slides only when needed and inside a lesson
  const { lessonSlideItems: slides, lessonSlidesLoading } = useSlidesByLessonId({
    searchText,
    lessonId: currentLessonId || "",
    isTrash: false,
    skip: !includeSlides || currentLessonId === null,
  });

  // Determine what to display based on current navigation state
  const isEmpty =
    includeSlides && currentLessonId ? slides.length === 0 : [...projects, ...lessons].length === 0;

  const currentParent = navigationStack[navigationStack.length - 1];

  /**
   * Navigate into a project folder
   */
  const handleProjectClick = (project: { id: string; name: string }) => {
    setNavigationStack((prev) => [...prev, { ...project, type: "project" }]);
    setCurrentLessonId(null);
    setCurrentProjectId(project.id);
  };

  /**
   * Navigate into a lesson (terminal navigation - lessons contain slides)
   */
  const handleLessonClick = (lesson: { id: string; name: string }) => {
    setCurrentLessonId(lesson.id);
  };

  /**
   * Navigate back one level in the hierarchy
   */
  const handleNavigateBack = () => {
    setNavigationStack((prev) => prev.slice(0, -1));
    const newParent = navigationStack[navigationStack.length - 2];

    if (newParent?.type === "project") {
      setCurrentProjectId(newParent.id);
      setCurrentLessonId(null);
    } else {
      setCurrentProjectId(null);
      setCurrentLessonId(null);
    }
  };

  /**
   * Navigate directly to a specific ancestor by stack index (-1 = root/home)
   */
  const navigateToIndex = (index: number) => {
    if (index === -1) {
      setCurrentProjectId(null);
      setCurrentLessonId(null);
      setNavigationStack([]);
    } else {
      const newStack = navigationStack.slice(0, index + 1);
      setNavigationStack(newStack);

      setCurrentProjectId(newStack[newStack.length - 1]?.id ?? null);
      setCurrentLessonId(null);
    }
  };

  /**
   * Reset navigation to root level
   */
  const resetNavigation = () => {
    setCurrentProjectId(null);
    setCurrentLessonId(null);
    setNavigationStack([]);
  };

  return {
    // Current state
    currentProjectId,
    currentLessonId,
    navigationStack,
    currentParent,

    // Data
    projects,
    lessonsWithLimitation,
    lessonsWithoutLimitation,
    slides: includeSlides ? slides : [],
    projectLoading,
    lessonLoading,
    lessonSlidesLoading,
    isEmpty,
    hasMoreProjects,
    hasMoreLesson,

    // Actions
    handleProjectClick,
    handleLessonClick,
    handleNavigateBack,
    navigateToIndex,
    resetNavigation,
    loadMoreLesson,
    loadMoreProjects,
  };
}
